// recaptcha.js

const recaptcha = (function () {
  let isScriptLoaded = false;
  let siteKey = null; // Will be set during initialization

  /**
   * Retrieves the user's language preference from a cookie.
   * @returns {string|null} The language code or null if not found.
   */
  function getUserLang() {
    let keyValue = document.cookie.match('(^|;) ?' + 'lang' + '=([^;]*)(;|$)');
    return keyValue ? unescape(keyValue[2]) : null;
  }

  /**
   * Checks if the user agent matches a specific iOS client pattern.
   * This logic might be specific to your application and less directly tied to reCAPTCHA's function.
   * @returns {boolean} True if the user agent matches, false otherwise.
   */
  function isRequestFromIosClient() {
    return navigator.userAgent === 'Mozilla/5.0'; // Be cautious: this is a very generic UA string.
  }

  /**
   * Loads the reCAPTCHA v3 script onto the page.
   * This function should only be called once.
   */
  function loadScript() {
    if (isScriptLoaded || !siteKey) {
      // Script is already loading/loaded, or siteKey is not set yet
      return;
    }

    isScriptLoaded = true; // Mark as loading/loaded
    let script = document.createElement('script');
    script.id = 'recaptcha-script'; // Assign an ID for easier lookup
    // Use the language obtained from the cookie or default to 'en'
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}&hl=${getUserLang() || 'en'}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      console.error('Recaptcha: Failed to load reCAPTCHA script.');
      isScriptLoaded = false; // Reset if loading failed
    };
    document.head.appendChild(script); // Append to head is generally better
  }

  /**
   * Initializes the reCAPTCHA handler. Sets the site key and loads the script.
   * This should be called once, typically on page load.
   * @param {string} key - Your reCAPTCHA v3 site key.
   */
  function init() {
    siteKey = window.google_captcha_key;
    loadScript();
  }

  /**
   * Executes reCAPTCHA v3 to obtain a token for a specific action.
   * This function assumes `init()` has been called and the script has loaded.
   * @param {string} action - The action string for reCAPTCHA (e.g., 'submit_form', 'login').
   * @returns {Promise<string|null>} A promise that resolves with the reCAPTCHA token, or null on error.
   */
  async function execute(callback) {
    if (!siteKey) {
      console.error('Recaptcha: Not initialized. Call init() first with your site key.');
      return null;
    }
    if (typeof grecaptcha === 'undefined' || typeof grecaptcha.execute !== 'function') {
      console.warn(
        'Recaptcha: grecaptcha.execute is not yet available. Script might still be loading or failed to load.'
      );
      // It's crucial that init() is called early in your page lifecycle for this to work reliably.
      return null;
    }

    try {
      const token = await grecaptcha.execute(siteKey, {action: 'submit_form'});
      if (!token) {
        console.error(`Recaptcha: Could not get token for action: submit_form`);
        return null;
      }
      setTokenToInput(token);
      handleCallback(token, callback);
      return token;
    } catch (error) {
      console.error(`Recaptcha: Error executing for action submit_form:`, error);
      return null;
    }
  }

  /**
   * Handles the existing callback logic from your original `mount` function.
   * This allows you to pass the obtained token to your application's specific callback.
   * @param {string} token - The reCAPTCHA token.
   * @param {function|object} callback - The callback function or object to be invoked.
   */
  function handleCallback(token, callback) {
    if (callback) {
      if (typeof callback === 'function') {
        callback(token);
      } else if (typeof callback === 'object' && callback !== null) {
        const callbackOption = localStorage.getItem('recaptchaCallbackOption');
        if (callbackOption && typeof callback[callbackOption] === 'function') {
          callback[callbackOption](token);
        } else {
          console.warn('Recaptcha: Callback option not found or not a function in callback object.');
        }
      }
    }
  }

  /**
   * Helper function to set the obtained token into a hidden input field.
   * @param {string} token - The reCAPTCHA token.
   * @param {string} inputId - The ID of the hidden input field (defaults to 'g-recaptcha-response').
   * @returns {boolean} True if the input was found and set, false otherwise.
   */
  function setTokenToInput(token, inputId = 'g-recaptcha-response') {
    const recaptchaInput = document.getElementById(inputId);
    if (recaptchaInput) {
      recaptchaInput.value = token;
      return true;
    } else {
      console.error(`Recaptcha: Hidden input field '${inputId}' not found.`);
      return false;
    }
  }

  // Public interface of the recaptcha object
  return {
    userLang: getUserLang,
    requestFromIosClient: isRequestFromIosClient, // Kept for compatibility
    init: init, // New method to initialize and load the script once
    execute: execute, // New method to get the token when an action occurs
    handleCallback: handleCallback, // Utility to trigger old callback logic
    setTokenToInput: setTokenToInput // Utility to set token in hidden input
  };
})();

// Export the recaptcha object for modular use (e.g., with Webpack/Rollup/ESM)
export {recaptcha};
